import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Objects } from '@sharedlib/common/lang/Objects';
import { type IconsRegistryShape } from './icons';

/** Many icons in one SVG: each a child whose `id` is the icon's name. */
export interface IconSprite {
  readonly names: ReadonlyArray<string>;
  readonly svg: string;
}

export function provideIcons(...iconRegistries: IconsRegistryShape[]): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => registerIcons(iconRegistries, null)),
  ]);
}

/**
 * {@link provideIcons}, with the icons the sprite holds registered from it in one go instead of one file
 * each. A registry's own entry would win over the set (`MatIconRegistry` asks named icons first), so the
 * sprite's names are not registered by file; every other name still is.
 *
 * The sprite is part of the bundle, so nothing is downloaded before the app starts. Should registering it
 * fail, every icon falls back to its own file, as without a sprite: the sprite never stops the app.
 */
export function provideIconsWithSprite(
  sprite: IconSprite,
  ...iconRegistries: IconsRegistryShape[]
): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      const registry = inject(MatIconRegistry);
      const sanitizer = inject(DomSanitizer);
      try {
        registry.addSvgIconSetLiteral(sanitizer.bypassSecurityTrustHtml(sprite.svg));
        registerIcons(iconRegistries, new Set(sprite.names), registry, sanitizer);
      } catch (error) {
        console.error('Icon sprite not registered; icons load one file each', error);
        registerIcons(iconRegistries, null, registry, sanitizer);
      }
    }),
  ]);
}

function registerIcons(
  iconRegistries: IconsRegistryShape[],
  skip: ReadonlySet<string> | null,
  registry = inject(MatIconRegistry),
  sanitizer = inject(DomSanitizer),
): void {
  for (const iconRegistry of iconRegistries) {
    Objects.forEach(iconRegistry, (name, path) => {
      if (!skip?.has(name)) {
        registry.addSvgIcon(name, sanitizer.bypassSecurityTrustResourceUrl(path));
      }
    });
  }
}
